import type BildebankenPluginType from '@nrk/bildebanken-plugin';
import type { Image } from '@nrk/bildebanken-plugin';
import type UtsnittsvelgerType from '@nrk/bildebanken-utsnittsvelger';
import type {
  CropOptions,
  DerivateParams,
} from '@nrk/bildebanken-utsnittsvelger';
import type { ImageRef } from '@pin/schemas/bildebanken';
import { firstValueFrom, of } from 'rxjs';
import { withIdToken } from '../../firebase/auth';
import { adminApiUrl } from '../../utils/context';

type OpenContext = {
  onSelect: (img: ImageRef) => void;
  width?: number;
  height?: number;
  bildebankenId?: string;
};

class BildebankenStore {
  isOpen = $state(false);
  #plugin: ReturnType<typeof BildebankenPluginType> | null = null;
  #utsnittsvelgerPromise: Promise<typeof UtsnittsvelgerType> | null = null;
  #openCtx: OpenContext | null = null;

  async #fetchProxiedDerivate(
    id: string,
    params: DerivateParams = {}
  ): Promise<{ location: string }> {
    const token = await firstValueFrom(withIdToken((t) => of(t)));
    const search = new URLSearchParams({
      imageId: id,
      ...Object.fromEntries(
        Object.entries(params).filter(([, value]) => value != null)
      ),
    });
    const res = await fetch(
      `${adminApiUrl}/api/bildebanken/derivate?${search}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(10_000),
      }
    );
    if (!res.ok) {
      throw new Error(`derivate proxy failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  }

  #findKaleidoId(image: Image): string | undefined {
    return (
      image.publications.find((p) => p.system === 'Kaleido')?.id ??
      image.publications[0]?.id
    );
  }

  #getUtsnittsvelger() {
    return (this.#utsnittsvelgerPromise ??=
      import('@nrk/bildebanken-utsnittsvelger')
        .then(({ default: uv }) => {
          uv.initialize({
            username: 'pin-klassens-klovn-admin',
            environment: 'prod',
            // Required by initialize, but never fires — we override getDerivate/getDerivateUrl
            // below to proxy through admin-api instead of exposing Azure AD credentials.
            bildebankenTokenCallback: () =>
              Promise.resolve({ accessToken: 'proxied-by-admin-api' }),
          });
          const getDerivate = (id: string, params?: DerivateParams) =>
            this.#fetchProxiedDerivate(id, params);
          uv.getDerivate = getDerivate;
          uv.getDerivateUrl = async (id, params) =>
            (await getDerivate(id, params)).location;
          return uv;
        })
        .catch((err) => {
          this.#utsnittsvelgerPromise = null;
          throw err;
        }));
  }

  async init(mountEl: HTMLElement) {
    if (this.#plugin) {
      return;
    }

    const [BildebankenPlugin] = await Promise.all([
      import('@nrk/bildebanken-plugin').then((m) => m.default),
      this.#getUtsnittsvelger(),
    ]);

    this.#plugin = BildebankenPlugin({
      system: 'pin-klassens-klovn-admin',
      environment: 'prod',
      requiredMetadata: ['altText'],
      resetViewOnClose: true,
      initialView: this.#openCtx?.bildebankenId
        ? { page: 'detail', imageId: this.#openCtx.bildebankenId }
        : { page: 'start' },

      onClose: () => this.close(),

      onChange: async (event, images) => {
        if (event !== 'select') {
          return;
        }

        const image = images[0];
        const openCtx = this.#openCtx;
        if (!image || !openCtx) {
          return;
        }

        const imageId = this.#findKaleidoId(image);
        if (!imageId) {
          console.error('No Kaleido publication id on selected image', image);
          return;
        }

        try {
          const { location } = await this.getDerivate(imageId, {
            ...(openCtx.width != null && { width: openCtx.width }),
            ...(openCtx.height != null && { height: openCtx.height }),
            format: 'webp',
          });

          openCtx.onSelect({
            src: location,
            alt: image.metadata.altText ?? '',
            imageId, // Kaleido ID (for cropper/derivates)
            bildebankenId: image.id, // Bildebanken ID (for detail view)
          });
        } catch (err) {
          console.error('Failed to fetch derivate URL', err);
        } finally {
          this.close();
        }
      },
    });
    this.#plugin.mount(mountEl);
  }

  open(
    onSelect: (img: ImageRef) => void,
    derivateSize: { width?: number; height?: number },
    currentBildebankenId?: string
  ) {
    this.#openCtx = {
      onSelect,
      width: derivateSize.width,
      height: derivateSize.height,
      bildebankenId: currentBildebankenId,
    };
    this.isOpen = true;

    if (currentBildebankenId && this.#plugin) {
      this.#plugin.setView({
        page: 'detail',
        imageId: currentBildebankenId,
      });
    }
  }

  close() {
    this.isOpen = false;
    this.#openCtx = null;
  }

  crop(el: HTMLElement, options: CropOptions | false) {
    this.#getUtsnittsvelger()
      .then((uv) => uv.crop(el, options))
      .catch((err) => console.error('Failed to update cropper state', err));
  }

  async getDerivate(id: string, params?: DerivateParams) {
    return (await this.#getUtsnittsvelger()).getDerivate(id, params);
  }

  destroy() {
    this.close();
    this.#plugin?.unmount();
    this.#plugin = null;
  }
}

export const bildebanken = new BildebankenStore();
